import Breadcrumbs from '@v2/shared/components/Breadcrumbs';
import ContentCard from '@v2/shared/components/ContentCard';
import PageHeading from '@v2/shared/components/PageHeading';
import useCurrentYear from '@v2/shared/lib/useCurrentYear';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';

/**
 * The date the page states, which is the date this wording ships. It is copy, not a
 * render-time value: `new Date()` here would move the date on every deploy and on every
 * cache miss, telling a visitor the policy changed on a day it did not. Change it by
 * hand, in the same commit that changes the words above it. Same rule as RefundPolicy.
 */
const LAST_UPDATED = 'September 12, 2026';

/** The address the DMCA requires notices to be sent to, laid out as the mockup has it. */
const ADDRESS = 'mt-0 border-0 border-l-[3px] border-solid border-slate-200 pl-4';

type Props = {
  /**
   * The year for the copyright notice, as the server rendered it. Handed straight to
   * `useCurrentYear`, which corrects it in the browser — this page's markup is kept for
   * a day, so a year the component worked out for itself would disagree with a day-old
   * render and cost a repaint.
   */
  currentYear?: number;
};

/** /copyright. */
export default function Copyright({ currentYear }: Props) {
  // Every internal link goes through buildUrl: these paths are written from the site
  // root, and AU serves from https://www.discountmags.com/au/, so rendered bare they
  // would walk an AU visitor onto the US site. The mailto and Ingram links are absolute.
  const { buildUrl } = useUrlGenerator();

  // Not `{labels.copyright.years}`: that token is what the three *legacy* footers use, and
  // it is a hand-edited `jss_labels` row rather than a clock — production still holds
  // `1999-2017`. Reading it here would put a nine-year-old year on a legal page.
  const copyrightYear = useCurrentYear(currentYear);

  const tosUrl = buildUrl('/terms');
  const privacyUrl = buildUrl('/privacy');

  return (
    <>
      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Copyright & Notices' }]} />

      <PageHeading
        eyebrow="Customer Information"
        intro={<span className="block text-xs opacity-75">Last updated: {LAST_UPDATED}</span>}
      >
        Copyright, Trademark, and Content Removal Policy
      </PageHeading>

      <div className="prose mt-6 flex flex-col gap-6">
        <ContentCard>
          <h2>1. About this policy</h2>

          <p>
            DiscountMags.com is an independent online retailer of magazine subscriptions, digital
            magazines, books, and related products. In this policy, &quot;DiscountMags.com,&quot;
            &quot;we,&quot; &quot;us,&quot; and &quot;our&quot; refer to the operator of the Site.
            We respect the intellectual property rights of others and expect the people who use our
            site to do the same.
          </p>

          <p>
            This policy explains how content on our site is sourced, how to report copyright or
            trademark infringement, how to request removal of an image of yourself or of other
            content, and what we do when we receive a request. It applies to the DiscountMags.com
            website and related services (the &quot;Site&quot;).
          </p>

          <p>
            This policy forms part of and is incorporated into <a href={tosUrl}>our Terms of Use</a>
            . By submitting a notice, counter notification, complaint, or request under this policy,
            you agree to <a href={tosUrl}>our Terms of Use</a>, including the dispute resolution,
            arbitration, and class action waiver provisions in them, which govern any dispute
            arising out of or relating to this policy or to any submission made under it.
          </p>
        </ContentCard>

        <ContentCard>
          <h2>2. How content on our site is sourced</h2>

          <p>
            We do not publish magazines or books. Most of the content on the Site comes from third
            parties: publishers, distributors, and the customers who post reviews.
          </p>

          <p>
            Print magazine subscriptions are obtained through publishers, authorized subscription
            agencies, and clearing houses, and each issue is mailed by the publisher or its
            fulfillment agent.
          </p>

          <p>
            Digital magazine subscriptions and single issues are delivered by our digital
            distribution partners under their agreements with publishers.
          </p>

          <p>
            Books and related products are listed and fulfilled by Ingram Content Group. Our book
            catalog is syndicated to us from Ingram&apos;s catalog feed, and Ingram ships every book
            order directly. We hold no book inventory. Ingram Book Company has confirmed to us in
            writing that DiscountMags.com has lawful title to the products it purchases from Ingram
            and is authorized, through its agreement with Ingram, to sell and deliver those products
            to its customers, including the right to display the images and trademarks associated
            with those products for the purpose of advertising and selling them.
          </p>

          <p>
            Magazine cover images are obtained from the publisher or its authorized distributors and
            are displayed, unaltered and at a size suitable for identifying the product, to show
            customers the product offered for sale. Product descriptions are supplied by our
            distribution partners or prepared by us.
          </p>

          <p>
            Customer reviews and other content posted by customers are the statements of the
            customers who post them. We do not adopt or endorse them.
          </p>

          <p>
            Because of this, a listing on the Site usually originates with a distributor or
            publisher rather than with us, and a claim about it is often best addressed at that
            source as well. Removing a listing from the Site does not remove the title from a
            distributor&apos;s catalog or from other retailers.
          </p>
        </ContentCard>

        <ContentCard>
          <h2>3. Reporting copyright infringement (DMCA notice)</h2>

          <p>
            If you believe that content on the Site infringes a copyright you own or are authorized
            to act for, send a written notice to our designated agent under the Digital Millennium
            Copyright Act, 17 U.S.C. § 512. Your notice must include:
          </p>

          <ol>
            <li>
              A physical or electronic signature of a person authorized to act on behalf of the
              owner of the copyright or of an exclusive right under it.
            </li>
            <li>
              Identification of the copyrighted work you claim has been infringed. If your notice
              covers multiple works, a representative list is sufficient.
            </li>
            <li>
              Identification of the material you claim is infringing and that you want removed or
              disabled, with enough information for us to locate it. Please include the full URL of
              each page on the Site and, for books, the ISBN.
            </li>
            <li>Your name, mailing address, telephone number, and, if available, your email address.</li>
            <li>
              A statement that you have a good faith belief that use of the material in the manner
              complained of is not authorized by the copyright owner, its agent, or the law.
            </li>
            <li>
              A statement that the information in the notice is accurate and, under penalty of
              perjury, that you are authorized to act on behalf of the owner of the copyright or of
              an exclusive right under it.
            </li>
          </ol>

          <p>Send notices to:</p>

          <p className={ADDRESS}>
            Copyright/Trademark Agent
            <br />
            DiscountMags.com
            <br />
            427 N Tatnall St, PMB 35314
            <br />
            Wilmington, DE 19801-2230
            <br />
            Email: <a href="mailto:copyrights@discountmags.com">copyrights@discountmags.com</a>
            <br />
            Phone: <a href="tel:+18006738187">800-673-8187</a>
          </p>

          <p>
            Email is the fastest way to reach us. Notices must be in writing; we cannot act on a
            telephone call alone, and notices sent to customer service or through chat rather than
            to the address above may be delayed.
          </p>

          <p>
            If your notice does not include the elements above, it may not be effective under the
            DMCA and we may ask you for more information before acting.
          </p>
        </ContentCard>

        <ContentCard>
          <h2>4. Book listings supplied by Ingram</h2>

          <p>
            If your notice concerns a book or other product supplied by Ingram Content Group, we
            encourage you to also submit Ingram&apos;s Copyright and Trademark Infringement Claims
            Form at{' '}
            {/* The url is the link text because the policy is read on paper as well as on
                screen. It is 48 unbroken characters, which is wider than a 375px phone —
                `break-words` is what stops it pushing the whole document sideways. */}
            <a href="https://ingramcontent.jotform.com/210803476928158" className="break-words">
              https://ingramcontent.jotform.com/210803476928158
            </a>
            . Ingram&apos;s catalog supplies thousands of retailers, so raising the claim with
            Ingram addresses the title at the distribution level and may result in its removal by
            other retailers that carry Ingram&apos;s catalog, while a notice to us affects only the
            Site. We will still act on a complete notice sent to us.
          </p>
        </ContentCard>

        <ContentCard>
          <h2>5. What we do when we receive a notice</h2>

          <p>
            When we receive a complete notice, we will remove or disable access to the identified
            material. Where the material was posted by a customer, we will notify that customer and
            tell them how to submit a counter notification. We may also raise the matter with the
            party that supplied the material and provide them with the notice or the information it
            contains.
          </p>

          <p>
            We may remove material in response to a notice whether or not a claim of infringement is
            well founded. Removal is not an admission that any content infringed and does not waive
            any right, defense, or protection available to us under 17 U.S.C. § 512 or otherwise.
            Removal of a listing does not by itself cancel subscriptions or orders already placed
            and does not affect the availability of the product from the publisher, distributor, or
            other retailers.
          </p>
        </ContentCard>

        <ContentCard>
          <h2>6. Counter notification</h2>

          <p>
            If material you posted was removed or disabled because of a copyright notice and you
            believe the removal was a mistake or a misidentification, you may send a written counter
            notification to our designated agent at the address above. It must include:
          </p>

          <ol>
            <li>Your physical or electronic signature.</li>
            <li>
              Identification of the material that was removed or disabled and the location where it
              appeared before removal.
            </li>
            <li>
              A statement, under penalty of perjury, that you have a good faith belief that the
              material was removed or disabled as a result of mistake or misidentification.
            </li>
            <li>
              Your name, address, and telephone number, and a statement that you consent to the
              jurisdiction of the Federal District Court for the judicial district in which your
              address is located (or, if your address is outside the United States, for any judicial
              district in which we may be found), and that you will accept service of process from
              the person who provided the original notice or that person&apos;s agent.
            </li>
          </ol>

          <p>
            We handle counter notifications in accordance with 17 U.S.C. § 512(g). Under that
            section, we send a copy of a valid counter notification to the person who submitted the
            original notice and inform them that the material will be restored in 10 business days,
            and the material is then restored not less than 10 nor more than 14 business days after
            we receive the counter notification, unless our designated agent first receives notice
            that the original notifier has filed an action seeking a court order to restrain the
            activity complained of.
          </p>

          <p>
            The consent to federal court jurisdiction in item 4 is required by the DMCA for counter
            notifications and does not otherwise limit the dispute resolution provisions of{' '}
            <a href={tosUrl}>our Terms of Use</a>.
          </p>
        </ContentCard>

        <ContentCard>
          <h2>7. Repeat infringers</h2>

          <p>
            We will terminate, in appropriate circumstances, the accounts of customers who are
            repeat infringers, and we may remove any content and disable any account at our
            discretion where we believe it infringes the rights of others.
          </p>
        </ContentCard>

        <ContentCard>
          <h2>8. Trademark complaints</h2>

          <p>
            If you believe a listing on the Site infringes a trademark you own, send a written
            complaint to the Copyright/Trademark Agent at the address above. Include:
          </p>

          <ol>
            <li>
              The trademark at issue, the jurisdiction where it is registered, and the registration
              number (or a description of the basis of your rights if the mark is unregistered).
            </li>
            <li>The full URL of each listing you believe infringes, and for books the ISBN.</li>
            <li>A description of how the listing infringes your mark.</li>
            <li>
              Your name, company, mailing address, telephone number, and email address, and your
              relationship to the trademark owner.
            </li>
            <li>
              A statement that you have a good faith belief that the use complained of is not
              authorized by the trademark owner and that the information in your complaint is
              accurate.
            </li>
          </ol>

          <p>
            We review trademark complaints individually and may remove the listing, pass the
            complaint to the supplier, ask you for more information, or decline to act. For products
            supplied by Ingram, Ingram&apos;s claim form in Section 4 also accepts trademark claims.
          </p>
        </ContentCard>

        <ContentCard>
          <h2>9. Requests to remove an image of you</h2>

          <p>
            Magazine covers sometimes show people who would prefer not to appear on our Site. If you
            are pictured on a cover or other image displayed on the Site and want it removed, you do
            not need to file a copyright notice. Email{' '}
            <a href="mailto:copyrights@discountmags.com">copyrights@discountmags.com</a> with:
          </p>

          <ol>
            <li>The full URL of each page where the image appears.</li>
            <li>A description of where you appear in the image.</li>
            <li>
              Your name and email address, and, if you are writing on behalf of the person pictured,
              your relationship to them.
            </li>
            <li>
              A statement that the information in your request is accurate and that you are the
              person pictured or are authorized to act on their behalf.
            </li>
          </ol>

          <p>
            We review these requests individually and may ask for information confirming your
            identity or authority. Where we act on a request we will generally remove the image or
            replace it with a substitute image, and the product listing itself will usually remain,
            since the publisher&apos;s product is still available for purchase. We may decline a
            request. We handle these requests as a matter of discretion and acting on one is not an
            acknowledgment of any legal obligation. Cover images are also displayed by publishers,
            distributors, and other retailers, and we cannot remove them from other sites.
          </p>
        </ContentCard>

        <ContentCard>
          <h2>10. Other content concerns</h2>

          <p>
            If you believe a customer review or other content on the Site is false, defamatory, or
            otherwise improper, email{' '}
            <a href="mailto:copyrights@discountmags.com">copyrights@discountmags.com</a> with the
            URL, the specific statement at issue, and why you believe it is improper. Customer
            reviews are the statements of the customers who post them. We review these requests
            individually, we may pass the request to the person who posted the content, and we may
            decline to act.
          </p>
        </ContentCard>

        <ContentCard>
          <h2>11. Information you send us</h2>

          <p>
            Information you send under this policy, including your name and contact details, is used
            to review and act on your submission. We may provide it, in whole or in part, to the
            party that supplied or posted the material at issue, to our distribution partners, and
            to our advisers. Where the DMCA requires it, a counter notification is provided to the
            person who submitted the original notice, and a notice is provided to the person who
            posted the material. By making a submission under this policy you consent to these
            disclosures. <a href={privacyUrl}>Our Privacy Policy</a> otherwise applies.
          </p>
        </ContentCard>

        <ContentCard>
          <h2>12. Misrepresentation</h2>

          <p>
            Under 17 U.S.C. § 512(f), anyone who knowingly materially misrepresents that material is
            infringing, or that material was removed by mistake or misidentification, is liable for
            any damages, including costs and attorneys&apos; fees, incurred by the alleged
            infringer, by any copyright owner or the owner&apos;s authorized licensee, or by us, as
            a result of our relying on the misrepresentation. Do not submit a notice or counter
            notification unless it is accurate.
          </p>
        </ContentCard>

        <ContentCard>
          <h2>13. Copyright and trademark notices</h2>

          <p>
            © {copyrightYear} DiscountMags.com. All rights reserved. The design, layout, software,
            our own product descriptions, and the compilation and arrangement of content on the Site
            are protected by United States and international copyright laws. No part of the Site may
            be reproduced, distributed, or displayed without our written permission, except that you
            may view and print pages for your own personal, non-commercial use.
          </p>

          <p>
            DiscountMags, DiscountMags.com, and DiscountLock are our trademarks. Magazine titles,
            book titles, cover art, logos, and other content identifying third-party products are
            the property of their respective publishers and rights holders and are displayed only to
            identify the products offered for sale. DiscountMags.com is an independent retailer and
            is not affiliated with, endorsed by, or an official site of any publisher. We claim no
            ownership of third-party content on the Site. If you want to use content that belongs to
            a publisher or other third party, contact that party for permission.
          </p>
        </ContentCard>

        <ContentCard>
          <h2>14. Changes and contact</h2>

          <p>
            This policy is general information about how to reach us and what we do with requests.
            It is not legal advice, and it does not change any right or obligation either of us has
            under the law. We may update it from time to time by posting a revised version on this
            page with a new &quot;Last updated&quot; date. Questions may be sent to{' '}
            <a href="mailto:copyrights@discountmags.com">copyrights@discountmags.com</a>.{' '}
            <a href={tosUrl}>Our Terms of Use</a> govern any dispute arising out of or relating to
            this policy.
          </p>
        </ContentCard>
      </div>
    </>
  );
}

Copyright.layoutName = 'Layouts.Institutional';
